/**
 * Runs one race on screen: the frame loop, the local run, the network sync, the other
 * racers (ghosts), the views and their layout, audio, haptics and the HUD state.
 *
 * Created by <GameScreen/> for one race and role (racer / spectator) and disposed on unmount.
 * Per frame: start the run at startAt → read the gamepads → step the simulation (input applied
 * at the tick it happened) → send positions and events → sample every racer → lay out and draw
 * the views → update the HUD (signals on change, fast numbers straight into the DOM a few times
 * a second).
 *
 * A race against the clock (`race.endAt`) ends at endAt for everybody: the run is stepped up
 * to that moment and no further, then it stands still, nothing more is sent, and the HUD says
 * «Время вышло!» until the shell shows the results. The timer counts the time left down.
 */
import {
  type BossKind,
  isTouchingOrb,
  MODE_BOXES,
  type PlayerId,
  type RaceEvent,
  type RaceProgressEntry,
  SPEEDS,
  type SpeedKey,
} from '@cube-rush/shared';
import { effect } from '@preact/signals';
import { createGameAudio } from '../audio/createGameAudio';
import type { GameAudio, MusicClock, MusicTrack } from '../audio/types';
import { ru } from '../i18n/ru';
import { GameInput } from '../input/GameInput';
import { type GamepadHub, gamepads } from '../input/gamepads';
import { AdaptiveQuality, pixelRatio } from '../render/quality';
import { Surface } from '../render/surface';
import { BladeWatch } from './blades';
import { BossWatch } from './boss';
import type { ActiveRace, GameScreenProps } from './contract';
import type { EngineDebugApi, EngineDevHooks, EngineScene, EngineStatus } from './debug';
import { HAPTIC_PATTERNS, PAD_RUMBLE, soundCueFor, vibrate } from './feedback';
import { FrameStats } from './frameStats';
import { GhostStore } from './ghosts';
import { HudDom } from './hud/hudDom';
import {
  HudModel,
  type HudPhase,
  MIN_STRIP_SPAN,
  type ModeIntro,
  type StripRacer,
  type ViewLabel,
} from './hud/hudModel';
import {
  arrangeViews,
  bottomInset,
  MAX_SPECTATOR_VIEWS,
  MAX_SPLIT_OTHERS,
  needsRotateHint,
  pickViews,
  type Rect,
  rankCandidates,
  resolveLayout,
  VIEW_GAP,
  type ViewCandidate,
} from './layout';
import { LevelResources } from './levelResources';
import { LocalRun, type RunEvent } from './localRun';
import { FrameLoop, FramePacer, type FrameScheduler } from './loop';
import { NetSync } from './netSync';
import {
  countdownValue,
  FINAL_BEEPS,
  GO_DISPLAY_MS,
  HURRY_MS,
  raceTimeAt,
  wireTime,
} from './raceClock';
import { findEntry, Racers } from './racers';
import {
  completedLevels,
  finishPlace,
  levelFraction,
  othersStillRacing,
  timedPlace,
} from './standings';
import { GameView, type LocalViewState, type ViewFrame, type ViewSubject } from './views';

const PICK_INTERVAL_MS = 1000;
const HUD_INTERVAL_MS = 60;
/** After finishing, the main view keeps the local player this long, then follows others. */
const FINISH_FOLLOW_DELAY_MS = 2500;
const NOTICE_MS = 3200;
const MAX_NOTICES = 3;
const LEVEL_FLASH_MS = 1800;
/** The banner of a whole level or a mode's portal (see its CSS), longer with the hint to read. */
const MODE_INTRO_MS = 2000;
const MODE_INTRO_HINT_MS = 3300;
/**
 * Modes whose portal brings a banner with how to play them (as whole levels have theirs,
 * introduceMode), the first time a race meets them.
 */
const PORTAL_HINTS = ['robot', 'spider'] as const;
type PortalHint = (typeof PORTAL_HINTS)[number];
/** Their banner comes up this long (game time) before the portal: as it comes into view. */
const PORTAL_HINT_LEAD_S = 1;
/** …on the first this many attempts that come up to it: by then the hint has been read. */
const PORTAL_HINT_SHOWS = 2;
/** The first robot or spider portal of a level (see modePortalsOf); `intro`: PortalObject.intro. */
interface ModePortal {
  x: number;
  intro: boolean;
}
/** The modes a whole level may be played in (LevelData.start.mode other than the cube). */
type WholeLevelMode = 'ball' | 'wave' | 'robot' | 'spider';
/** Attempts of a level are told apart by level × this + attempt. */
const ATTEMPT_KEY = 100_000;
const QUIT_CONFIRM_MS = 3500;
/** The finish fanfare waits this long for the place before it plays without one. */
const FINISH_CUE_WAIT_MS = 1500;
/** When the time is up the music fades out this long. */
const TIME_UP_FADE_MS = 1200;
/**
 * The racer's music begins with every attempt (beat 0 on its first tick): the countdown arms
 * it this long before the start, a death fades the old tune out over most of the pause
 * before the respawn, a completed level over most of the pause before the next one.
 */
const MUSIC_ARM_MS = 1000;
const DEATH_FADE_MS = 450;
const LEVEL_FADE_MS = 400;

export interface GameControllerOptions {
  dev?: EngineDevHooks;
  /** Frame scheduler (tests drive frames by hand). */
  scheduler?: FrameScheduler;
  /**
   * Real time for pacing the frames of fast screens (default performance.now, also under the
   * dev harness's time warp; tests: their clock).
   */
  frameTime?: () => number;
  /** Audio factory (tests). */
  createAudio?: () => GameAudio;
  /** The gamepads (tests: a hub with fake pads; default: the app's). */
  pads?: GamepadHub;
}

export class GameController {
  readonly hud: HudModel;
  readonly dom = new HudDom();
  private props: GameScreenProps;
  private readonly race: ActiveRace;
  /** A race against the clock ends at this server time; null for a race by levels. */
  private readonly endAt: number | null;
  /** Against the clock: its length (ms). */
  private readonly durationMs: number | null;
  private readonly me: PlayerId;
  private readonly levels: LevelResources | null;
  private readonly run: LocalRun | null;
  private readonly sync: NetSync | null;
  private readonly others: readonly PlayerId[];
  private readonly ghosts = new GhostStore();
  private readonly racers: Racers;
  private readonly clock: () => number;
  private readonly loop: FrameLoop;
  private readonly quality = new AdaptiveQuality();
  private readonly dev: EngineDevHooks | undefined;
  private readonly createAudio: () => GameAudio;
  private readonly pads: GamepadHub;

  private root: HTMLElement | null = null;
  private surface: Surface | null = null;
  private input: GameInput | null = null;
  private audio: GameAudio | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private readonly cleanups: Array<() => void> = [];
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private disposed = false;

  private views: GameView[] = [];
  private viewRects: Rect[] = [];
  private viewport = { w: 0, h: 0 };
  /** CSS px at the top of the screen covered by the HUD bar. */
  private hudInset = 0;
  /** Where the HUD controls along the bottom edge are (CSS px in the game area). */
  private bottomBoxes: Rect[] = [];
  /** The adaptive resolution changed: resize the canvas before the next frame is drawn. */
  private resizePending = false;
  private layoutDirty = true;
  private nextPickAt = 0;
  private picks: PlayerId[] = [];
  private watching: PlayerId | null = null;

  /** The race was already running when the screen mounted (reload mid-race). */
  private readonly midRace: boolean;
  private started = false;
  private lastFrame = -1;
  private time = 0;
  private raceTime = 0;
  private serverOffset = 0;
  private lastCountdown: number | null = null;
  private finishedAt: number | null = null;
  private finishTm = 0;
  private finishCueAt: number | null = null;
  /** The time of a race against the clock is up: the run stands still, nothing is sent. */
  private timeIsUp = false;
  /** ms until endAt as of the latest frame (null for a race by levels). */
  private timeLeft: number | null = null;
  /** The last of the final seconds that beeped (5 … 1). */
  private lastBeep: number | null = null;
  /** The race strip's span was set once (later changes ease the avatars over). */
  private spanSet = false;
  private musicLevel = -1;
  /** The racer's music is armed on the attempt clock (see armMusic). */
  private musicArmed = false;
  /** The racer's music clock, reused every frame. */
  private readonly musicClock: MusicClock = { position: 0, scale: 1, ago: 0 };
  /** Main modes of the whole levels this race has shown its hint for. */
  private readonly modesIntroduced = new Set<WholeLevelMode>();
  /** Bosses whose level's banner this race has shown with how to play it (once each). */
  private readonly bossesIntroduced = new Set<BossKind>();
  /**
   * The level in which the race first meets the robot and the spider: their banner comes up at
   * its portal on the first attempts of it (PORTAL_HINT_SHOWS), and never later in the race — nor
   * at all where that level is a whole level of the mode (its banner comes as it starts).
   */
  private readonly hintLevels = new Map<PortalHint, number>();
  /** The attempt the banner came up in last (see ATTEMPT_KEY) and how often it did, per mode. */
  private readonly hintsShown = new Map<PortalHint, { attempt: number; times: number }>();
  /** The first robot and spider portal of each level seen (null: none). */
  private readonly modePortals = new Map<number, Record<PortalHint, ModePortal | null>>();
  private lastTimeScale = 1;
  private reducedMotion = false;

  private readonly runEvents: RunEvent[] = [];
  private readonly blades = new BladeWatch();
  private readonly bossWatch = new BossWatch();
  private readonly localState: LocalViewState = { collected: new Set(), orbFocus: null };
  private readonly orbFocus = { x: 0, y: 0, half: 0.5 };
  private readonly frame: ViewFrame;

  private nextHudAt = 0;
  private noticeKey = 0;
  /** Counts "Точно сдаться?" questions: an expiring one must not end a newer one. */
  private quitQuestion = 0;
  private readonly stats = new FrameStats();
  /** Overall progress of a racer, for the progress strip. */
  private readonly progressOf = (id: PlayerId): number => this.overall(id);
  /**
   * Measures how long a frame keeps the main thread busy, painting included: a message
   * posted from the frame callback is handled only after the browser has drawn the frame.
   */
  private readonly busyProbe = typeof MessageChannel === 'function' ? new MessageChannel() : null;
  private frameStart = 0;
  private lastBusy = 0;
  private readonly api: EngineDebugApi;

  constructor(props: GameScreenProps, options: GameControllerOptions = {}) {
    this.props = props;
    this.race = props.race;
    this.endAt = props.race.endAt;
    this.durationMs = this.endAt === null ? null : Math.max(0, this.endAt - props.race.startAt);
    this.me = props.me.id;
    this.dev = options.dev;
    this.createAudio = options.createAudio ?? createGameAudio;
    this.pads = options.pads ?? gamepads;
    const hasLevels = props.race.levels.length > 0;
    this.levels = hasLevels ? new LevelResources(props.race.levels) : null;
    const spectator = props.spectator || !hasLevels;
    this.run = spectator ? null : new LocalRun(props.race.levels, props.race.resumeLevel);
    this.sync = this.run ? new NetSync(props.net, props.race.startAt) : null;
    this.others = props.race.participants.filter((id) => id !== this.me);
    this.racers = new Racers(
      this.me,
      () => this.props.me.profile,
      () => this.props.members.peek(),
      this.others,
      this.levels,
      props.race.levels.length,
      this.endAt !== null,
    );
    this.frame = {
      time: 0,
      dt: 0,
      scale: 1,
      showGhosts: true,
      reducedMotion: false,
      racers: this.racers.list,
      season: 'none',
      quality: 1,
    };
    this.hud = new HudModel(props.race.levels.length, spectator, this.durationMs, this.pads.style);
    this.clock = options.dev?.now ?? (() => performance.now());
    this.loop = new FrameLoop(
      (now) => this.onFrame(now),
      this.clock,
      options.scheduler,
      new FramePacer(),
      options.frameTime ?? (() => performance.now()),
    );
    this.midRace = props.net.serverNow() >= props.race.startAt;
    if (this.endAt !== null) this.timeLeft = this.endAt - props.net.serverNow();
    if (this.run) this.hud.level.value = this.run.sim.levelIndex;
    this.api = this.createDebugApi();
  }

  /** Latest props (the shell re-renders with new callbacks). Race and role never change. */
  setProps(props: GameScreenProps): void {
    this.props = props;
  }

  mount(root: HTMLElement, canvas: HTMLCanvasElement): void {
    this.root = root;
    this.surface = new Surface(canvas);
    this.input = new GameInput(
      root,
      {
        onBoost: (slot) => this.useBoost(slot),
        onMenu: () => this.toggleMenu(),
        onBack: () => {
          if (this.hud.menuOpen.peek()) this.setMenu(false);
        },
        onMute: () => this.props.voice.toggleMute(),
        onSwitch: (step) => this.switchRacer(step),
        onAreaPress: () => this.onAreaPress(),
      },
      this.clock,
      this.pads,
    );
    try {
      this.audio = this.createAudio();
    } catch {
      this.audio = null;
    }
    this.reducedMotion =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    const { settings, voice, members, progress, net } = this.props;
    this.cleanups.push(
      effect(() => {
        const s = settings.value;
        this.audio?.setVolumes({ sfx: s.sfxVolume, music: s.musicVolume });
        this.levels?.setSeason(s.season);
        this.layoutDirty = true;
      }),
      effect(() => {
        const speaking = voice.speaking.value;
        let others = false;
        for (const id of speaking) if (id !== this.me) others = true;
        this.audio?.setDucked(others);
      }),
      effect(() => {
        this.racers.refreshArts(members.value);
        this.refreshStrip();
      }),
      effect(() => {
        this.onProgress(progress.value);
      }),
      // Stamped with the race time of their arrival: how late they come sets the ghosts' delay.
      net.onStates((players) => this.ghosts.receive(players, net.serverNow() - this.race.startAt)),
      net.onEvent((from, event) => this.onRemoteEvent(from, event)),
    );
    // Android's Back opens the menu (resume, settings, give up) instead of leaving the app.
    const { registerBack } = this.props;
    if (registerBack) this.cleanups.push(registerBack(() => this.toggleMenu()));

    if (typeof ResizeObserver === 'function') {
      // Resizing clears the canvas, and observers run after this frame was drawn: draw it
      // again right away, laid out for the new size, instead of showing an empty frame.
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.measure()) return;
        this.updateLayout(this.clock());
        this.render(0);
      });
      this.resizeObserver.observe(root);
    }
    this.measure();
    this.dev?.onReady?.(this.api);
    if (this.busyProbe) {
      this.busyProbe.port1.onmessage = () => {
        this.lastBusy = performance.now() - this.frameStart;
      };
    }
    this.loop.start();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.loop.stop();
    if (this.busyProbe) {
      this.busyProbe.port1.onmessage = null;
      this.busyProbe.port1.close();
      this.busyProbe.port2.close();
    }
    this.input?.dispose();
    this.resizeObserver?.disconnect();
    for (const view of this.views) view.dispose();
    this.views = [];
    // Big canvases are freed at once, not whenever the garbage collector runs (iOS caps them).
    this.surface?.release();
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    try {
      this.audio?.stopMusic(0);
      this.audio?.dispose();
    } catch {
      // Audio problems must never break leaving the game.
    }
    this.audio = null;
  }

  // ---- HUD actions ----

  useBoost(slot: number): void {
    const run = this.run;
    if (!run || !this.started || this.timeIsUp || run.sim.phase !== 'running') return;
    if (run.sim.useBoost(slot) !== null) this.updateHudSignals();
  }

  toggleMenu(): void {
    this.setMenu(!this.hud.menuOpen.peek());
  }

  setMenu(open: boolean): void {
    this.hud.menuOpen.value = open;
    if (!open) this.disarmQuit();
    this.audio?.play({ kind: 'ui' });
    this.syncJumpEnabled();
  }

  openSettings(): void {
    this.setMenu(false);
    this.props.onOpenSettings?.();
  }

  /**
   * "Сдаться" (against the clock "Закончить"): asks once more ("Точно сдаться?" for
   * QUIT_CONFIRM_MS), then leaves. Only the question on screen counts: closing the menu takes
   * it back. Spectators, finished racers and anybody after the time is up leave at once.
   */
  requestQuit(): void {
    if (!this.run || this.run.sim.finished || this.timeIsUp || this.hud.confirmQuit.peek()) {
      this.disarmQuit();
      this.hud.menuOpen.value = false;
      this.props.onQuit();
      return;
    }
    const key = ++this.quitQuestion;
    this.hud.confirmQuit.value = true;
    this.audio?.play({ kind: 'ui' });
    this.later(QUIT_CONFIRM_MS, () => {
      // Only this question expires here, not one asked again later.
      if (this.quitQuestion === key) this.hud.confirmQuit.value = false;
    });
  }

  private disarmQuit(): void {
    this.quitQuestion++;
    this.hud.confirmQuit.value = false;
  }

  /** Spectators (and finished racers): watch the previous / next racer. */
  switchRacer(step: -1 | 1): void {
    if (!this.isWatching()) return;
    const list = this.watchList();
    if (list.length === 0) return;
    const index = this.watching ? list.indexOf(this.watching) : -1;
    const next = list[(index + step + list.length) % list.length]!;
    if (next === this.watching) return;
    this.watching = next;
    this.layoutDirty = true;
    this.audio?.play({ kind: 'ui' });
  }

  // ---- frame ----

  private onFrame(now: number): void {
    const workStart = performance.now();
    // How busy the previous frame kept the main thread (its painting included).
    const previousBusy = this.lastBusy;
    this.frameStart = workStart;
    const interval = this.lastFrame < 0 ? 0 : now - this.lastFrame;
    const dt = Math.min(0.1, interval / 1000);
    this.lastFrame = now;
    this.time += dt;
    const serverNow = this.props.net.serverNow();
    this.serverOffset = serverNow - now;
    this.raceTime = serverNow - this.race.startAt;
    this.timeLeft = this.endAt === null ? null : this.endAt - serverNow;
    if (this.resizePending) {
      this.resizePending = false;
      this.measure();
    }

    // A screen that comes up after the end (a reload in the last moment) never starts a run.
    const over = this.timeLeft !== null && this.timeLeft <= 0;
    // The first beat of the music falls on the start: armed a moment before it, during the
    // countdown (a reload mid-race arms it as its run starts, below).
    if (
      this.run &&
      !this.started &&
      !over &&
      !this.musicArmed &&
      this.raceTime >= -MUSIC_ARM_MS &&
      this.raceTime < 0
    ) {
      this.armMusic(this.run.sim.levelIndex, now);
    }
    if (!this.started && this.raceTime >= 0 && !over) this.startRace(now);
    this.updateCountdown();
    // Up to date before the pads are read below: a pad's press in the race's first frame counts.
    this.syncJumpEnabled();
    if (this.run && this.started && !this.timeIsUp) {
      // Against the clock the run goes up to endAt (on the engine clock) and not a tick further.
      this.stepRun(now, this.timeLeft === null ? now : Math.min(now, now + this.timeLeft));
    } else {
      // Nothing to step: the pads still open the menu, move its focus, switch the racer watched.
      this.input?.pollPads(now);
    }
    if (this.timeLeft !== null) this.updateTimeLeft(this.timeLeft);
    this.syncMusic(now);
    // The others are shown ghosts.delayMs in the past. After the end they play out up to it and
    // stand where the time found them (they do not go stale).
    this.ghosts.adapt(dt * 1000, this.raceTime);
    const ghostTime = this.raceTime - this.ghosts.delayMs;
    const sampleTime = this.durationMs === null ? ghostTime : Math.min(ghostTime, this.durationMs);
    this.racers.sample(this.run, now, this.ghosts, sampleTime, this.props.progress.peek());
    if (this.layoutDirty || now >= this.nextPickAt) this.updateLayout(now);
    this.render(dt);
    this.syncJumpEnabled();
    if (now >= this.nextHudAt) this.updateHud(now);

    const work = performance.now() - workStart;
    this.stats.record(interval, work, previousBusy);
    this.busyProbe?.port2.postMessage(0);
    const busy = this.busyProbe ? Math.max(work, previousBusy) : work;
    // Not now: resizing the canvas after drawing would show an empty frame.
    if (this.quality.record(now, interval, busy) !== null) this.resizePending = true;
  }

  private startRace(now: number): void {
    this.started = true;
    if (this.run && this.input) {
      // A fresh race starts at startAt exactly (the first frame may come a little later);
      // a reload mid-race starts now, at the beginning of the resumed level.
      this.run.start(this.midRace ? now : now - this.raceTime, this.input.jump);
    }
    // The racer's music is on the attempt clock (armed in the countdown, or now: a reload
    // mid-race starts the level's tune at its first beat); a spectator's keeps its own time.
    if (this.run) {
      if (!this.musicArmed) this.armMusic(this.run.sim.levelIndex, now);
    } else {
      this.playMusic(this.watchedLevel());
    }
    // A reload in the middle of a whole level or a boss level: its banner too.
    if (this.run) this.introduceMode(this.run.sim.levelIndex);
    this.updateHudSignals();
  }

  private updateCountdown(): void {
    if (this.midRace) return;
    // After the start the countdown only runs out: "Старт!" for GO_DISPLAY_MS, then nothing,
    // even if a clock-sync correction moves the race time back before the start.
    const value = !this.started
      ? countdownValue(this.raceTime)
      : this.lastCountdown !== null && this.raceTime < GO_DISPLAY_MS
        ? 0
        : null;
    if (value === this.lastCountdown) return;
    this.lastCountdown = value;
    this.hud.countdown.value = value;
    if (value !== null && value <= 3) this.audio?.play({ kind: 'countdown', secondsLeft: value });
  }

  /** Steps the local run for a frame at `now`, up to `until` (endAt, if it came first). */
  private stepRun(now: number, until: number): void {
    const run = this.run!;
    const input = this.input;
    if (!input) return;
    run.prepare(now);
    this.dev?.beforeStep?.(this.api, now);
    // Gamepads have no events: they are read now, right before the ticks they act on (each
    // change carries the pad's own time, so a press still lands on its tick).
    input.pollPads(now);
    const events = this.runEvents;
    events.length = 0;
    run.advance(until, input.jump, events);
    for (const e of events) this.onRunEvent(e, now);
    this.watchModePortals();
    this.watchBlades();
    this.watchBoss();
    this.sync?.onEvents(events, run, this.serverOffset);
    this.sync?.update(now, run, this.serverOffset);
    const scale = run.sim.timeScale;
    if (scale !== this.lastTimeScale) {
      this.lastTimeScale = scale;
      this.audio?.setTimeScale(scale);
    }
    if (events.length > 0) this.updateHudSignals();
  }

  private onRunEvent(e: RunEvent, now: number): void {
    const run = this.run!;
    const sim = run.sim;
    const event = e.event;
    this.dev?.onEvent?.(event, e.tick, e.level, e.attempt);
    const cue = soundCueFor(
      event,
      event.type === 'portal' && this.slowsDown(e.level, event.object),
    );
    if (cue) this.audio?.play(cue);
    const haptics = this.props.settings.peek().haptics;
    switch (event.type) {
      case 'death':
        if (haptics) {
          vibrate(HAPTIC_PATTERNS.death);
          // A pad shakes too (where the browser can): the crash is felt in the hands.
          this.input?.rumble(PAD_RUMBLE.death);
        }
        // The tune fades out; the next attempt's begins on its first tick.
        this.restartMusic(e.level, DEATH_FADE_MS, now);
        break;
      case 'levelComplete':
        if (haptics) vibrate(HAPTIC_PATTERNS.levelComplete);
        // Against the clock the strip shows the levels done.
        if (this.endAt !== null) this.refreshStrip();
        if (event.level < this.race.levels.length - 1) {
          // The next level's tune, armed for its first tick; and its number flashes up.
          this.restartMusic(event.level + 1, LEVEL_FADE_MS, now);
          const key = ++this.noticeKey;
          this.hud.levelFlash.value = { key, level: event.level + 1 };
          this.later(LEVEL_FLASH_MS, () => {
            if (this.hud.levelFlash.peek()?.key === key) this.hud.levelFlash.value = null;
          });
        }
        break;
      case 'levelStart':
        // Armed when the last level was completed (anything else: arm it now).
        if (this.musicLevel !== event.level) this.armMusic(event.level, now);
        this.introduceMode(event.level);
        break;
      case 'raceFinished':
        this.finishedAt = now;
        this.finishTm = wireTime(raceTimeAt(e.time, this.serverOffset, this.race.startAt));
        this.finishCueAt = now + FINISH_CUE_WAIT_MS;
        this.audio?.stopMusic(1200);
        this.onProgress(this.props.progress.peek());
        break;
      default:
        break;
    }
    const art = this.racers.art(this.me);
    const s = sim.state;
    for (const view of this.views) {
      if (view.subject.kind === 'local') {
        view.localEvent(event, art, s.x, s.y, s.gravity, e.teleport);
      }
    }
  }

  /**
   * «Шарик!» / «Стрелочка!» / «Робот!» / «Паук!» as a whole level starts, «Босс!» as a boss level
   * does; the first time in the race (for each mode, each boss) with how to play it. The robot and
   * the spider show it once in the race, either way: on their first whole level, or at their first
   * portal (watchModePortals) — a whole level after that portal shows the title alone, and their
   * portals after a whole level of theirs nothing.
   */
  private introduceMode(levelIndex: number): void {
    const level = this.levels?.get(levelIndex).level;
    if (level?.boss !== undefined) {
      const hint = !this.bossesIntroduced.has(level.boss);
      this.bossesIntroduced.add(level.boss);
      this.showModeIntro('boss', hint, level.boss);
      return;
    }
    const mode = level?.start.mode;
    if (mode !== 'ball' && mode !== 'wave' && mode !== 'robot' && mode !== 'spider') return;
    let hint = !this.modesIntroduced.has(mode);
    if (mode === 'robot' || mode === 'spider') {
      // Met at a portal on an earlier level already: its banner showed how to play it there.
      const met = this.hintLevels.get(mode);
      if (met !== undefined && met !== levelIndex) hint = false;
      this.hintLevels.set(mode, met ?? levelIndex);
    }
    this.modesIntroduced.add(mode);
    this.showModeIntro(mode, hint);
  }

  /**
   * «Робот!» / «Паук!» as the local player comes up to the race's first portal of the mode (it
   * comes into view, PORTAL_HINT_LEAD_S ahead), on the first PORTAL_HINT_SHOWS attempts that get
   * there, and never later in the race. Where the level introduces the mode (the portal is marked,
   * PortalObject.intro: the section after it is the gentlest and waits for the banner), the banner
   * with how to play it, over the middle of the view; elsewhere the race's first section of the
   * mode is built as any other, and only its title comes up, small, along the top bar — out of the
   * way of what has to be jumped.
   */
  private watchModePortals(): void {
    const run = this.run;
    if (run?.sim.phase !== 'running') return;
    const index = run.sim.levelIndex;
    const portals = this.modePortalsOf(index);
    const s = run.sim.state;
    for (const mode of PORTAL_HINTS) {
      const portal = portals?.[mode];
      if (!portal || (this.hintLevels.get(mode) ?? index) !== index) continue;
      this.hintLevels.set(mode, index);
      const attempt = index * ATTEMPT_KEY + run.sim.attempt;
      const shown = this.hintsShown.get(mode);
      if (shown !== undefined && (shown.attempt === attempt || shown.times >= PORTAL_HINT_SHOWS)) {
        continue;
      }
      // The portal acts as the player's centre passes its middle.
      const ahead = portal.x + 0.5 - s.x;
      if (ahead < 0 || ahead > SPEEDS[s.speed] * PORTAL_HINT_LEAD_S) continue;
      this.hintsShown.set(mode, { attempt, times: (shown?.times ?? 0) + 1 });
      if (portal.intro) this.showModeIntro(mode, true);
      else this.showModeBadge(mode);
    }
  }

  /** The swish of a saw blade or a rotor's arm the local player slips past (see BladeWatch). */
  private watchBlades(): void {
    const run = this.run;
    if (run?.sim.phase !== 'running' || !this.levels) return;
    const index = run.sim.levelIndex;
    const world = this.levels.get(index).world;
    if (this.blades.check(world, run.sim.state, index * ATTEMPT_KEY + run.sim.attempt)) {
      this.audio?.play({ kind: 'blade' });
    }
  }

  /**
   * A boss level's boss as the local player hears it (see BossWatch): its roar as the level
   * begins and its growl at every new attempt, each attack's warning (one at a time) and go, its
   * crash as the player escapes.
   */
  private watchBoss(): void {
    const run = this.run;
    if (run?.sim.phase !== 'running' || !this.levels) return;
    const index = run.sim.levelIndex;
    const bundle = this.levels.get(index);
    if (bundle.level.boss === undefined) return;
    const s = run.sim.state;
    const cues = this.bossWatch.check(
      index,
      bundle.level,
      bundle.world.attacks,
      run.sim.attempt,
      s.tick,
      s.x,
    );
    let warned = false;
    for (const cue of cues) {
      // (A fireball and the fire it leaves are warned of together: one sound.)
      if (cue.kind === 'attack' && cue.warn) {
        if (warned) continue;
        warned = true;
      }
      this.audio?.play(cue);
    }
  }

  /** The first robot and spider portal of a level (found once per level). */
  private modePortalsOf(index: number): Record<PortalHint, ModePortal | null> | null {
    const levels = this.levels;
    if (!levels) return null;
    let found = this.modePortals.get(index);
    if (!found) {
      found = { robot: null, spider: null };
      for (const o of levels.get(index).level.objects) {
        if (o.type !== 'portal') continue;
        for (const mode of PORTAL_HINTS) {
          const first = found[mode];
          if (o.portal === `mode-${mode}` && (first === null || o.x < first.x)) {
            found[mode] = { x: o.x, intro: o.intro === true };
          }
        }
      }
      this.modePortals.set(index, found);
    }
    return found;
  }

  /** A mode's banner (a boss level's: `boss`), with how to play it (`hint`) or without. */
  private showModeIntro(mode: ModeIntro['mode'], hint: boolean, boss?: ModeIntro['boss']): void {
    const key = ++this.noticeKey;
    this.hud.modeIntro.value = boss === undefined ? { key, mode, hint } : { key, mode, hint, boss };
    this.later(hint ? MODE_INTRO_HINT_MS : MODE_INTRO_MS, () => {
      if (this.hud.modeIntro.peek()?.key === key) this.hud.modeIntro.value = null;
    });
  }

  /** The robot's or the spider's title alone, small, along the top bar (see watchModePortals). */
  private showModeBadge(mode: PortalHint): void {
    const key = ++this.noticeKey;
    this.hud.modeIntro.value = { key, mode, hint: false, badge: true };
    this.later(MODE_INTRO_MS, () => {
      if (this.hud.modeIntro.peek()?.key === key) this.hud.modeIntro.value = null;
    });
  }

  /** Whether a portal of a level is a speed portal that slows the player down. */
  private slowsDown(levelIndex: number, object: number): boolean {
    const bundle = this.levels?.get(levelIndex);
    const o = bundle?.level.objects[object];
    if (!bundle || o?.type !== 'portal' || !o.portal.startsWith('speed-')) return false;
    const after = SPEEDS[Number(o.portal.slice(6)) as SpeedKey];
    return after !== undefined && after < bundle.guide.speedAt(o.x + o.w / 2 - 1e-6);
  }

  // ---- layout ----

  /** Reads the screen size and sizes the canvas; true if the canvas was resized (cleared). */
  private measure(): boolean {
    const root = this.root;
    if (!root) return false;
    const w = Math.max(1, Math.round(root.clientWidth));
    const h = Math.max(1, Math.round(root.clientHeight));
    if (w !== this.viewport.w || h !== this.viewport.h) {
      this.viewport = { w, h };
      this.layoutDirty = true;
      this.quality.reset(this.clock());
      this.hud.rotateHint.value = needsRotateHint(w, h);
    }
    const ratio =
      this.dev?.pixelRatio ??
      pixelRatio(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, this.quality.quality);
    const resized = this.surface?.resize(w, h, ratio) ?? false;
    this.measureHud();
    return resized;
  }

  /**
   * How much of the top of the screen the HUD bar covers (views keep their content below),
   * and where the controls along the bottom edge are.
   */
  private measureHud(): void {
    const bar = this.dom.topBar;
    const root = this.root;
    if (!bar || !root) return;
    const area = root.getBoundingClientRect();
    const inset = Math.max(0, Math.round(bar.getBoundingClientRect().bottom - area.top + 4));
    if (inset !== this.hudInset) {
      this.hudInset = inset;
      this.layoutDirty = true;
    }
    const boxes: Rect[] = [];
    for (const control of this.dom.bottomControls.values()) {
      const box = control.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0) continue;
      boxes.push({ x: box.left - area.left, y: box.top - area.top, w: box.width, h: box.height });
    }
    this.bottomBoxes = boxes;
  }

  /**
   * Spectators watch the others; so do racers a moment after their finish, as long as
   * somebody is still racing (otherwise they keep their own view at the finish).
   */
  private isWatching(): boolean {
    if (!this.run) return true;
    // When the time is up the racer's own view stays: that is where the time found them.
    if (this.timeIsUp) return false;
    if (this.finishedAt === null || this.clock() - this.finishedAt < FINISH_FOLLOW_DELAY_MS) {
      return false;
    }
    return this.candidates().some((c) => c.active);
  }

  private candidates(): ViewCandidate[] {
    return this.racers.candidates(this.props.progress.peek(), this.run);
  }

  /**
   * Others to switch between when watching: the active racers by progress (leaders first),
   * or everybody if nobody is racing any more.
   */
  private watchList(): PlayerId[] {
    const candidates = this.candidates();
    const active = rankCandidates(candidates, null);
    if (active.length > 0) return active;
    return [...candidates].sort((a, b) => b.progress - a.progress).map((c) => c.id);
  }

  private updateLayout(now: number): void {
    this.layoutDirty = false;
    this.nextPickAt = now + PICK_INTERVAL_MS;
    this.measureHud();
    const { w, h } = this.viewport;
    if (!this.levels || w <= 1 || h <= 1) {
      this.setViews([], []);
      return;
    }
    const settings = this.props.settings.peek();
    const candidates = this.candidates();
    const activeCount = candidates.filter((c) => c.active).length;
    const mode = resolveLayout(settings.layout, w, h, activeCount);
    const watching = this.isWatching();
    let subjects: ViewSubject[];
    let main = false;
    if (!watching) {
      this.picks =
        mode === 'split'
          ? pickViews(candidates, this.overall(this.me), MAX_SPLIT_OTHERS, this.picks)
          : [];
      subjects = [{ kind: 'local' }, ...this.picks.map((id) => remote(id))];
      main = subjects.length > 1;
    } else if (mode === 'split') {
      this.picks = pickViews(candidates, null, MAX_SPECTATOR_VIEWS, this.picks);
      subjects = this.picks.map((id) => remote(id));
    } else {
      // Keep watching the chosen racer while they race; otherwise the leader.
      const current = candidates.find((c) => c.id === this.watching);
      if (!current?.active) {
        const best = rankCandidates(candidates, null)[0];
        if (best || !current) this.watching = best ?? this.watchList()[0] ?? null;
      }
      subjects = this.watching ? [remote(this.watching)] : [];
    }
    if (subjects.length === 0) {
      if (this.run) subjects = [{ kind: 'local' }];
      else if (this.others[0]) subjects = [remote(this.others[0])];
    }
    const rects = arrangeViews(subjects.length, w, h, main);
    this.setViews(subjects, rects);

    // HUD: names on the views of other racers, who is watched, where the main view is.
    const labels: ViewLabel[] = [];
    this.views.forEach((view, i) => {
      if (view.subject.kind !== 'remote') return;
      const id = view.subject.id;
      const profile = this.racers.art(id).profile;
      const snap = this.racers.get(id);
      labels.push({
        key: `${i}:${id}`,
        id,
        avatar: profile.avatar,
        name: profile.name,
        level: Math.min((snap?.level ?? 0) + 1, this.race.levels.length),
        stale: snap?.stale ?? false,
        finished: this.overall(id) >= 1,
        rect: rects[i]!,
      });
    });
    if (!sameLabels(this.hud.labels.peek(), labels)) this.hud.labels.value = labels;
    const watchedId = watching && mode === 'single' ? this.watching : null;
    this.hud.watching.value = watchedId;
    this.hud.canSwitch.value = watching && mode === 'single' && this.others.length > 1;
    const mainRect = rects[0] ?? { x: 0, y: 0, w, h };
    const previous = this.hud.mainRect.peek();
    if (!sameRect(previous, mainRect)) this.hud.mainRect.value = mainRect;
    if (!this.run) this.playMusic(this.watchedLevel());
  }

  private setViews(subjects: readonly ViewSubject[], rects: readonly Rect[]): void {
    const levels = this.levels;
    if (!levels) {
      for (const view of this.views) view.dispose();
      this.views = [];
      this.viewRects = [];
      return;
    }
    const old = this.views;
    const next: GameView[] = [];
    subjects.forEach((subject, i) => {
      // Keep the view (and its camera) of a subject that stays on screen.
      let view = old.find((v) => !next.includes(v) && sameSubjectKey(v.subject, subject));
      view ??= new GameView(subject, levels);
      view.setSubject(subject);
      const rect = rects[i]!;
      view.setRect(rect, rect.y === 0 ? this.hudInset : 0, bottomInset(rect, this.bottomBoxes));
      next.push(view);
    });
    for (const view of old) if (!next.includes(view)) view.dispose();
    this.views = next;
    this.viewRects = [...rects];
  }

  // ---- drawing ----

  private render(dt: number): void {
    const surface = this.surface;
    if (!surface) return;
    const ctx = surface.begin(this.viewRects, VIEW_GAP);
    if (!ctx) {
      // No canvas (tests): keep the cameras and effects running anyway.
      for (const view of this.views) this.updateView(view, dt);
      return;
    }
    for (const view of this.views) {
      this.updateView(view, dt);
      view.draw(ctx, this.frame);
    }
  }

  private updateView(view: GameView, dt: number): void {
    const frame = this.frame;
    frame.time = this.time;
    frame.dt = dt;
    frame.scale = this.surface?.scale ?? 1;
    const settings = this.props.settings.peek();
    frame.showGhosts = settings.showGhosts;
    frame.reducedMotion = this.reducedMotion;
    frame.season = settings.season;
    frame.quality = this.quality.quality;
    if (view.subject.kind === 'local') {
      const subject = this.racers.get(this.me) ?? null;
      view.update(subject, this.race.resumeLevel, frame, this.localViewState());
    } else {
      const id = view.subject.id;
      const subject = this.racers.get(id) ?? null;
      const entry = findEntry(this.props.progress.peek(), id);
      view.update(subject, entry?.lvl ?? 0, frame, null);
    }
  }

  private localViewState(): LocalViewState | null {
    const run = this.run;
    if (!run) return null;
    const sim = run.sim;
    const s = sim.state;
    const state = this.localState;
    state.collected = sim.collectedPickups;
    if (sim.phase === 'running' && s.alive && isTouchingOrb(sim.world, s)) {
      this.orbFocus.x = s.x;
      this.orbFocus.y = s.y;
      this.orbFocus.half = MODE_BOXES[s.mode].outer.w / 2;
      state.orbFocus = this.orbFocus;
    } else {
      state.orbFocus = null;
    }
    return state;
  }

  // ---- racers, progress, events ----

  private onProgress(progress: readonly RaceProgressEntry[]): void {
    this.refreshStrip();
    this.layoutDirty = true;
    const timeUp = this.hud.timeUp.peek();
    if (timeUp && timeUp.levels !== null) {
      // The place once the server has every level the run completed (one may still be on its
      // way when the time runs out).
      const mine = findEntry(progress, this.me);
      const place = mine && mine.lvl >= timeUp.levels ? timedPlace(progress, this.me) : null;
      if (place !== timeUp.place) this.hud.timeUp.value = { ...timeUp, place };
    }
    if (this.finishedAt === null) return;
    const place = finishPlace(progress, this.me);
    const waiting = othersStillRacing(progress, this.me);
    this.hud.finish.value = { timeMs: this.finishTm, place, waiting };
    if (place !== null && this.finishCueAt !== null) {
      this.finishCueAt = null;
      this.audio?.play({ kind: 'raceFinish', place });
    }
  }

  private onRemoteEvent(from: PlayerId, event: RaceEvent): void {
    if (event.k !== 'level' || from === this.me) return;
    const last = event.lvl >= this.race.levels.length - 1;
    this.audio?.play({ kind: 'opponent', event: last ? 'finish' : 'level' });
    const profile = this.racers.art(from).profile;
    const key = ++this.noticeKey;
    const text = last ? ru.game.noticeFinish : ru.game.noticeLevel(event.lvl + 1);
    const notices = [
      ...this.hud.notices.peek(),
      { key, avatar: profile.avatar, name: profile.name, text },
    ];
    this.hud.notices.value = notices.slice(-MAX_NOTICES);
    this.later(NOTICE_MS, () => {
      this.hud.notices.value = this.hud.notices.peek().filter((n) => n.key !== key);
    });
  }

  /** Overall progress 0..1 of a racer (from the live position, else the server's level). */
  private overall(id: PlayerId): number {
    return this.racers.overall(id, this.props.progress.peek(), this.run);
  }

  private refreshStrip(): void {
    const strip = this.racers.strip(this.race.participants, this.props.progress.peek(), this.run);
    if (!sameStrip(this.hud.strip.peek(), strip)) this.hud.strip.value = strip;
  }

  /**
   * Against the clock the strip's track is as long as the race so far: always a level beyond
   * the furthest racer, at least MIN_STRIP_SPAN. It only grows; the avatars ease over.
   */
  private updateStripSpan(now: number): void {
    const highest = this.racers.highestLevel(this.props.progress.peek(), this.run);
    const span = Math.max(MIN_STRIP_SPAN, highest + 2, this.hud.stripSpan.peek());
    if (this.spanSet && span === this.hud.stripSpan.peek()) return;
    if (this.spanSet) this.dom.rescaleStrip(now);
    this.spanSet = true;
    this.racers.setSpan(span);
    this.hud.stripSpan.value = span;
  }

  // ---- a race against the clock ----

  /** The last seconds: red timer, a beep each second (5 … 1), then the end. */
  private updateTimeLeft(timeLeft: number): void {
    if (this.timeIsUp) return;
    if (timeLeft <= 0) {
      this.onTimeUp();
      return;
    }
    const hurry = this.started && timeLeft <= HURRY_MS;
    if (hurry !== this.hud.hurry.peek()) this.hud.hurry.value = hurry;
    const seconds = Math.ceil(timeLeft / 1000);
    if (this.started && seconds <= FINAL_BEEPS && seconds !== this.lastBeep) {
      this.lastBeep = seconds;
      this.audio?.play({ kind: 'countdown', secondsLeft: seconds });
    }
  }

  /**
   * The time is up, for everybody at once: the run stands where it is (the level in progress
   * does not count), nothing more is sent, no jumps and no boosts, the menu closes, the music
   * fades under a horn, and «Время вышло!» with the levels done (the place once the server has
   * them all). The view stays on the local player.
   */
  private onTimeUp(): void {
    this.timeIsUp = true;
    this.sync?.stop();
    this.disarmQuit();
    this.hud.menuOpen.value = false;
    this.hud.hurry.value = false;
    // A screen that came up after the end (a reload) shows it without the horn.
    if (this.started) {
      this.audio?.stopMusic(TIME_UP_FADE_MS);
      this.audio?.play({ kind: 'timeUp' });
    }
    const run = this.run;
    // Every level done (cannot happen with the levels a server hands out): the finish card stays.
    if (!run?.sim.finished) {
      this.hud.timeUp.value = { levels: run ? completedLevels(run.sim) : null, place: null };
    }
    this.layoutDirty = true;
    this.updateHudSignals();
    this.syncJumpEnabled();
    this.onProgress(this.props.progress.peek());
  }

  // ---- HUD ----

  private phase(): HudPhase {
    if (!this.run) return 'spectating';
    if (this.run.sim.finished) return 'finished';
    if (this.timeIsUp) return 'timeUp';
    return this.started ? 'racing' : 'countdown';
  }

  /** Signals that change with simulation events (cheap: unchanged values do not re-render). */
  private updateHudSignals(): void {
    const hud = this.hud;
    hud.phase.value = this.phase();
    const run = this.run;
    if (run) {
      const sim = run.sim;
      hud.level.value = sim.levelIndex;
      hud.attempt.value = sim.attempt;
      const inventory = sim.inventory;
      const shown = hud.inventory.peek();
      if (shown.length !== inventory.length || shown.some((b, i) => b !== inventory[i])) {
        hud.inventory.value = [...inventory];
      }
      const active = sim.activeBoosts;
      const shownActive = hud.activeBoosts.peek();
      if (
        shownActive.length !== active.length ||
        shownActive.some((b, i) => b !== active[i]!.type)
      ) {
        hud.activeBoosts.value = active.map((b) => b.type);
      }
      hud.boostsReady.value = this.started && !this.timeIsUp && sim.phase === 'running';
    } else {
      hud.level.value = this.watchedLevel();
    }
  }

  private updateHud(now: number): void {
    this.nextHudAt = now + HUD_INTERVAL_MS;
    this.updateHudSignals();
    if (this.durationMs !== null && this.timeLeft !== null) {
      // Against the clock: the time left, the whole length before the start, 0 at the end.
      const left = this.timeIsUp ? 0 : Math.min(this.durationMs, Math.max(0, this.timeLeft));
      this.dom.setTimeLeft(left);
      this.updateStripSpan(now);
    } else {
      // Race timer: stops at the local finish, otherwise runs with the clock.
      this.dom.setTimer(
        this.finishedAt !== null ? this.finishTm : this.started ? Math.max(0, this.raceTime) : 0,
      );
    }
    this.dom.setLevelBar(this.levelFractionShown());
    this.dom.placeMarkers(this.progressOf, now);
    if (this.run) this.dom.setBoostTimers(this.run.sim.activeBoosts);
    if (this.finishCueAt !== null && now >= this.finishCueAt) {
      this.finishCueAt = null;
      this.audio?.play({ kind: 'raceFinish', place: null });
    }
  }

  private levelFractionShown(): number {
    const levels = this.levels;
    if (!levels) return 0;
    if (this.run) {
      const sim = this.run.sim;
      return sim.finished || sim.phase === 'levelComplete' ? 1 : sim.progress;
    }
    const snap = this.watching ? this.racers.get(this.watching) : undefined;
    return snap ? levelFraction(levels.get(snap.level).level, snap.x) : 0;
  }

  private syncJumpEnabled(): void {
    const run = this.run;
    const enabled =
      run !== null &&
      this.started &&
      !this.timeIsUp &&
      !run.sim.finished &&
      !this.hud.menuOpen.peek() &&
      !this.hud.rotateHint.peek();
    this.input?.setJumpEnabled(enabled);
  }

  private onAreaPress(): void {
    if (this.isWatching() && this.hud.watching.peek() !== null) this.switchRacer(1);
  }

  private watchedLevel(): number {
    const id = this.views[0]?.subject.kind === 'remote' ? this.views[0].subject.id : this.watching;
    const snap = id ? this.racers.get(id) : undefined;
    return snap?.level ?? this.race.resumeLevel;
  }

  /** A spectator's music: the watched racer's level, keeping its own time. */
  private playMusic(levelIndex: number): void {
    const levels = this.levels;
    if (!levels || !this.started || this.timeIsUp || levelIndex === this.musicLevel) return;
    this.musicLevel = levelIndex;
    this.audio?.startMusic(this.trackOf(levelIndex));
    this.audio?.setTimeScale(this.lastTimeScale);
  }

  /**
   * The racer's music for the attempt that is running or about to begin on `levelIndex`:
   * from its first bar, its first beat on the attempt's first tick (the clock's position 0;
   * see LocalRun.attemptTime). syncMusic keeps it on the game's beat from then on.
   */
  private armMusic(levelIndex: number, now: number): void {
    if (!this.levels || this.timeIsUp) return;
    this.musicArmed = true;
    this.musicLevel = levelIndex;
    this.audio?.startMusic(this.trackOf(levelIndex), this.updateMusicClock(now));
  }

  /** A new attempt is coming (a death, the next level): the old tune fades, the new is armed. */
  private restartMusic(levelIndex: number, fadeMs: number, now: number): void {
    if (!this.musicArmed) return;
    this.audio?.stopMusic(fadeMs);
    this.armMusic(levelIndex, now);
  }

  /** Every frame: the racer's music follows the attempt clock (turbo, slow motion, lost time). */
  private syncMusic(now: number): void {
    const run = this.run;
    if (!run || !this.musicArmed || this.timeIsUp || run.sim.finished) return;
    this.audio?.syncMusic(this.updateMusicClock(now));
  }

  /** The attempt clock as of `now`: before the start, the countdown runs up to it. */
  private updateMusicClock(now: number): MusicClock {
    const clock = this.musicClock;
    const run = this.run;
    if (run && this.started) {
      clock.position = run.attemptTime;
      clock.scale = run.sim.timeScale;
      clock.ago = Math.max(0, now - run.lastTickTime);
    } else {
      clock.position = this.raceTime / 1000;
      clock.scale = 1;
      clock.ago = 0;
    }
    return clock;
  }

  private trackOf(levelIndex: number): MusicTrack {
    const level = this.levels!.get(levelIndex).level;
    const season = this.props.settings.peek().season;
    return {
      bpm: level.bpm,
      theme: level.theme,
      seed: level.seed,
      season,
      ...(level.boss === undefined ? {} : { boss: level.boss }),
    };
  }

  private later(ms: number, callback: () => void): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      if (!this.disposed) callback();
    }, ms);
    this.timers.add(timer);
  }

  // ---- debug API (dev harness, tests) ----

  private createDebugApi(): EngineDebugApi {
    return {
      status: () => this.status(),
      scene: () => this.scene(),
      press: (source, time) => this.input?.jump.press(source, time),
      release: (source, time) => this.input?.jump.release(source, time),
      tickStartTime: (tick) => this.run?.tickStartTime(tick) ?? Number.NaN,
      useBoost: (slot) => this.useBoost(slot),
      fastForward: (ticks, held) => {
        const run = this.run;
        if (!run || !this.started) return;
        const events = this.runEvents;
        events.length = 0;
        run.fastForward(ticks, held, events);
        // Input queued for the skipped ticks is stale now.
        this.input?.jump.discard();
        for (const e of events) this.onRunEvent(e, this.clock());
        this.sync?.onEvents(events, run, this.serverOffset);
        this.updateHudSignals();
      },
      frames: () => this.stats.samples(),
      clearFrames: () => this.stats.clear(),
      setMenu: (open) => this.setMenu(open),
    };
  }

  private status(): EngineStatus {
    const run = this.run;
    const sim = run?.sim;
    return {
      started: this.started,
      spectator: run === null,
      phase: sim?.phase ?? null,
      level: sim?.levelIndex ?? this.watchedLevel(),
      attempt: sim?.attempt ?? 0,
      tick: sim?.state.tick ?? 0,
      nextTickTime: run?.lastTickTime ?? 0,
      now: this.clock(),
      timeScale: sim?.timeScale ?? 1,
      mode: sim?.state.mode ?? null,
      x: sim?.state.x ?? 0,
      y: sim?.state.y ?? 0,
      alive: sim?.state.alive ?? false,
      deaths: sim?.deaths ?? 0,
      finished: sim?.finished ?? false,
      inventory: sim ? [...sim.inventory] : [],
      activeBoosts: sim ? sim.activeBoosts.map((b) => b.type) : [],
      held: this.input?.jump.held ?? false,
      raceTime: this.raceTime,
      timeUp: this.timeIsUp,
      timeLeft: this.timeLeft === null ? null : Math.max(0, this.timeLeft),
      lostTime: run?.lostTime ?? 0,
      views: this.views.length,
      pixelRatio: this.surface?.scale ?? 1,
      frames: this.stats.count,
    };
  }

  private scene(): EngineScene {
    return {
      others: this.others.flatMap((id) => {
        const snap = this.racers.get(id);
        return snap
          ? [{ id, level: snap.level, x: snap.x, alive: snap.alive, stale: snap.stale }]
          : [];
      }),
      ghostDelay: this.ghosts.delayMs,
      views: this.views.map((view) => ({
        follows: view.subject.kind === 'local' ? this.me : view.subject.id,
        level: view.levelIndex,
        ghosts: view.ghostIds(this.frame),
      })),
    };
  }
}

function remote(id: PlayerId): ViewSubject {
  return { kind: 'remote', id };
}

function sameSubjectKey(a: ViewSubject, b: ViewSubject): boolean {
  if (a.kind === 'local' || b.kind === 'local') return a.kind === b.kind;
  return a.id === b.id;
}

function sameRect(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

function sameLabels(a: readonly ViewLabel[], b: readonly ViewLabel[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (l, i) =>
        l.key === b[i]!.key &&
        l.name === b[i]!.name &&
        l.avatar === b[i]!.avatar &&
        l.level === b[i]!.level &&
        l.stale === b[i]!.stale &&
        l.finished === b[i]!.finished &&
        sameRect(l.rect, b[i]!.rect),
    )
  );
}

function sameStrip(a: readonly StripRacer[], b: readonly StripRacer[]): boolean {
  return (
    a.length === b.length &&
    a.every((r, i) => {
      const o = b[i]!;
      return (
        r.id === o.id &&
        r.avatar === o.avatar &&
        r.name === o.name &&
        r.color === o.color &&
        r.finished === o.finished &&
        r.quit === o.quit &&
        r.offline === o.offline &&
        r.levels === o.levels
      );
    })
  );
}
